import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  Users,
  Target,
  TrendingUp,
  ShoppingCart,
  Search,
  Zap,
  DollarSign,
  RefreshCw,
  UserCheck,
  AlertCircle,
  MessageCircle,
  Info,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { StreamingChatWidget } from '../../components/chat/StreamingChatWidget';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

interface Segment {
  id: string;
  name: string;
  slug: string;
  description: string;
  userCount: number;
  percentage: number;
  characteristics: string[];
  criteria?: Array<{
    metric: string;
    operator: string;
    value: string;
    unit?: string;
  }>;
  avgOrderValue?: number;
  conversionRate?: number;
}

interface UserSegmentAssignment {
  userId: string;
  userEmail?: string;
  primarySegment: string;
  confidence: number;
  secondarySegments: Array<{
    segment: string;
    confidence: number;
  }>;
  lastUpdated: string;
  behaviorSummary: string;
}

interface SegmentInsight {
  segment: string;
  topProducts: Array<{ name: string; views: number }>;
  topCategories: Array<{ name: string; percentage: number }>;
  behaviorPatterns: string[];
  recommendations: string[];
}

const COLORS = [
  '#8B5CF6', // Purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const SEGMENT_ICONS = {
  'deal-hunters': DollarSign,
  'research-enthusiasts': Search,
  'quick-buyers': Zap,
  'premium-shoppers': ShoppingCart,
  'electronics-enthusiasts': Target,
};

// Persona images for each segment type
const SEGMENT_IMAGES = {
  'deal-hunters': 'https://assets.pagemind.ai/personas/deal-hunter.jpg',
  'research-enthusiasts': 'https://assets.pagemind.ai/personas/researcher.jpg',
  'quick-buyers': 'https://assets.pagemind.ai/personas/quick-buyer.jpg',
  'premium-shoppers': 'https://assets.pagemind.ai/personas/premium-shopper.jpg',
  'electronics-enthusiasts': 'https://assets.pagemind.ai/personas/tech-enthusiast.jpg',
  'window-shoppers': 'https://assets.pagemind.ai/personas/window-shopper.jpg',
  'loyal-customers': 'https://assets.pagemind.ai/personas/loyal-customer.jpg',
};

// Fallback to generated avatars if CDN images not available
const getSegmentImage = (segmentSlug: string, segmentName: string) => {
  if (SEGMENT_IMAGES[segmentSlug]) {
    return SEGMENT_IMAGES[segmentSlug];
  }
  // Generate a unique avatar based on the segment name
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(segmentName)}&background=8B5CF6&color=fff&size=200&font-size=0.4&bold=true`;
};

const SEGMENT_RECOMMENDATIONS = {
  'premium-shoppers': "Curate exclusive or high-quality toys (e.g., handmade, eco-friendly, educational sets). Offer gift-wrapping, personal shopping support, and early access to limited editions. Reinforce craftsmanship and story behind products.",
  'deal-hunters': "Highlight discounts, bundle deals, and clearance items prominently. Create urgency with limited-time offers and showcase savings percentages. Consider loyalty programs with exclusive discounts for repeat customers.",
  'research-enthusiasts': "Provide detailed product specifications, comparisons, and educational content. Include customer reviews, ratings, and Q&A sections. Offer comprehensive buying guides and expert recommendations.",
  'quick-buyers': "Streamline the checkout process with one-click purchasing and saved payment methods. Show popular items and bestsellers prominently. Use clear CTAs and minimize steps to purchase.",
  'electronics-enthusiasts': "Feature tech-enabled toys, STEM products, and smart toys. Provide detailed tech specs, compatibility information, and setup guides. Highlight innovation and educational benefits.",
  'window-shoppers': "Use engaging visuals, interactive content, and storytelling to capture interest. Implement wish lists and cart abandonment emails. Offer first-time buyer discounts to encourage conversion.",
  'loyal-customers': "Create VIP programs with exclusive benefits, early access to new products, and special events. Personalize communications and product recommendations based on purchase history.",
};

export default function CustomerSegments() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [segmentUsers, setSegmentUsers] = useState<UserSegmentAssignment[]>([]);
  const [segmentInsights, setSegmentInsights] = useState<SegmentInsight | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // No permission check needed - customer segments is available to all authenticated users

  // Fetch segments when site changes
  useEffect(() => {
    if (selectedSite) {
      fetchSegments();
    }
  }, [selectedSite]);

  const fetchSegments = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(`/analytics/segments/distribution/${siteId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch segments');
      }
      
      const data = await response.json();
      setSegments(data.distribution || []);
      
      // Select first segment if available
      if (data.distribution && data.distribution.length > 0 && !selectedSegment) {
        setSelectedSegment(data.distribution[0].slug);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      toast.error('Failed to load segment data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSegmentUsers = async (segmentSlug: string) => {
    if (!selectedSite) return;
    
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/analytics/segments/${siteId}/${segmentSlug}/users`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch segment users');
      }
      
      const data = await response.json();
      setSegmentUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching segment users:', error);
      toast.error('Failed to load segment users');
    }
  };

  const fetchSegmentInsights = async (segmentSlug: string) => {
    if (!selectedSite) return;
    
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/analytics/segments/${siteId}/${segmentSlug}/insights`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch segment insights');
      }
      
      const data = await response.json();
      setSegmentInsights(data.insights);
    } catch (error) {
      console.error('Error fetching segment insights:', error);
    }
  };

  const triggerSegmentRefresh = async () => {
    if (!selectedSite) return;
    
    setRefreshing(true);
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(`/analytics/segments/${siteId}/refresh`, {
        method: 'POST'
      });
      
      // Always try to parse the response
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        // Use the hint from the server if available
        const message = data.hint || data.message || 'Failed to refresh segments';
        toast.error(message);
        return;
      }
      
      toast.success(data.message || 'Segment refresh initiated');
      
      // Only refresh data if users were actually queued
      if (data.usersQueued > 0) {
        setTimeout(() => {
          fetchSegments();
        }, 2000);
      }
    } catch (error) {
      console.error('Error refreshing segments:', error);
      toast.error('Failed to refresh segments');
    } finally {
      setRefreshing(false);
    }
  };

  const searchUser = async () => {
    if (!userSearchQuery.trim() || !selectedSite) return;
    
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/analytics/segments/${siteId}/user/${encodeURIComponent(userSearchQuery)}`
      );
      
      if (!response.ok) {
        throw new Error('User not found');
      }
      
      const data = await response.json();
      
      // Show user segment info
      toast.success(
        `User is in ${data.primarySegment} segment (${Math.round(data.confidence * 100)}% confidence)`
      );
      
      // Switch to that segment
      setSelectedSegment(data.primarySegmentSlug);
    } catch (error) {
      console.error('Error searching user:', error);
      toast.error('User not found or has no segment assignment');
    }
  };

  // Update segment data when selection changes
  useEffect(() => {
    if (selectedSegment && selectedSite) {
      fetchSegmentUsers(selectedSegment);
      fetchSegmentInsights(selectedSegment);
    }
  }, [selectedSegment, selectedSite]);

  const selectedSegmentData = segments.find(s => s.slug === selectedSegment);
  const SegmentIcon = selectedSegmentData ? 
    SEGMENT_ICONS[selectedSegmentData.slug as keyof typeof SEGMENT_ICONS] || Users : 
    Users;

  // Show loading state while auth is being checked
  if (authLoading || !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Customer Segments</h1>
        <p className="text-gray-600">
          Analyze customer behavior patterns and segment distribution
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex gap-2 items-center flex-1">
          <Input
            placeholder="Search user by ID or email..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchUser()}
            className="max-w-xs"
          />
          <Button variant="outline" onClick={searchUser}>
            <Search className="w-4 h-4" />
          </Button>
        </div>

        <Button
          variant="outline"
          onClick={triggerSegmentRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Segments
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Segment Distribution */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Segment Distribution</CardTitle>
              <CardDescription>User distribution across segments</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={segments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={70}
                      fill="#8884d8"
                      dataKey="userCount"
                      label={({ percentage }) => `${percentage.toFixed(1)}%`}
                      nameKey="name"
                    >
                      {segments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value} users (${((value / segments.reduce((sum, s) => sum + s.userCount, 0)) * 100).toFixed(1)}%)`,
                        props.payload.name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend separately */}
              <div className="flex flex-wrap justify-center gap-3">
                {segments.map((segment, index) => (
                  <div key={segment.slug} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: segment.color || COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm">{segment.name}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {segments.map((segment, index) => (
                  <button
                    key={segment.slug}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedSegment === segment.slug
                        ? 'bg-purple-100 border-purple-500 border dark:bg-purple-900/20 dark:border-purple-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setSelectedSegment(segment.slug)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: segment.color || COLORS[index % COLORS.length] }}
                        />
                        <span className="font-semibold text-sm">{segment.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{segment.userCount}</div>
                        <div className="text-xs text-gray-500">
                          {segment.percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Segment Details */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SegmentIcon className="w-8 h-8 text-purple-600" />
                  <div>
                    <CardTitle className="text-xl">{selectedSegmentData?.name || 'Select a segment'}</CardTitle>
                    <CardDescription className="text-base">
                      {selectedSegmentData?.description || 'Choose a segment to view details'}
                    </CardDescription>
                  </div>
                </div>
                {selectedSegmentData && (
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {selectedSegmentData.userCount} users
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedSegmentData && (
                <>
                  {/* Agent Recommendations - Now shown first */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">AI-Powered Insights & Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-start gap-4">
                        <img 
                          src="/crew-agents/customer-segmentation-agent.jpg" 
                          alt="Customer Segmentation Agent"
                          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-700">Customer Segmentation Agent</p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex items-center gap-1"
                              onClick={() => {
                                // Navigate to the customer segmentation agent in insights hub
                                navigate('/insights/hub/customer-segmentation');
                              }}
                            >
                              <MessageCircle className="w-3 h-3" />
                              Let's talk
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed mb-3">
                            {SEGMENT_RECOMMENDATIONS[selectedSegment] || 
                             "I'm analyzing this segment to provide personalized recommendations. Each segment has unique characteristics that require tailored marketing strategies to maximize engagement and conversion."}
                          </p>
                          
                          {/* Quick metrics analysis */}
                          <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Conversion Rate</p>
                              <p className="text-lg font-semibold">
                                {selectedSegmentData.conversionRate 
                                  ? selectedSegmentData.conversionRate.toFixed(2) 
                                  : '12.50'}%
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">vs site avg: 8.30%</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Order Value</p>
                              <p className="text-lg font-semibold">
                                ${selectedSegmentData.avgOrderValue 
                                  ? selectedSegmentData.avgOrderValue.toFixed(2) 
                                  : '125.50'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">vs site avg: $95.20</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Tabs defaultValue="users" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="users">Users</TabsTrigger>
                      <TabsTrigger value="insights">Insights</TabsTrigger>
                      <TabsTrigger value="characteristics">Characteristics</TabsTrigger>
                    </TabsList>

                  <TabsContent value="users" className="space-y-4">
                    <div className="max-h-96 overflow-y-auto">
                      {segmentUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          No users in this segment yet
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {segmentUsers.map((user) => (
                            <div
                              key={user.userId}
                              className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <UserCheck className="w-4 h-4 text-green-600" />
                                    <span className="font-medium">
                                      {user.userEmail || user.userId}
                                    </span>
                                    <Badge variant="secondary">
                                      {Math.round(user.confidence * 100)}% match
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {user.behaviorSummary}
                                  </p>
                                  {user.secondarySegments.length > 0 && (
                                    <div className="flex gap-2">
                                      <span className="text-xs text-gray-500">Also matches:</span>
                                      {user.secondarySegments.map((sec) => (
                                        <Badge key={sec.segment} variant="outline" className="text-xs">
                                          {sec.segment} ({Math.round(sec.confidence * 100)}%)
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {format(new Date(user.lastUpdated), 'MMM d, h:mm a')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="insights" className="space-y-4">
                    {segmentInsights ? (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base">Top Products</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {segmentInsights.topProducts.map((product, i) => (
                                  <div key={i} className="flex justify-between items-center">
                                    <span className="text-sm truncate flex-1">
                                      {product.name}
                                    </span>
                                    <Badge variant="secondary">{product.views}</Badge>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base">Category Interests</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {segmentInsights.topCategories.map((category, i) => (
                                  <div key={i}>
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{category.name}</span>
                                      <span>{category.percentage}%</span>
                                    </div>
                                    <Progress value={category.percentage} className="h-2" />
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">Behavior Patterns</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-2">
                              {segmentInsights.behaviorPatterns.map((pattern, i) => (
                                <Badge key={i} variant="outline">
                                  {pattern}
                                </Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">Marketing Recommendations</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-2">
                              {segmentInsights.recommendations.map((rec, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
                                  <span className="text-sm">{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                        Loading insights...
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="characteristics" className="space-y-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Segment Characteristics</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {(selectedSegmentData.characteristics || [
                            'Views multiple products before purchasing',
                            'Responds well to email campaigns',
                            'Prefers detailed product information',
                            'Active during evening hours'
                          ]).map((char, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-purple-600 rounded-full" />
                              <span className="text-sm">{char}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Segment Criteria */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Segment Criteria</CardTitle>
                        <CardDescription>
                          Behavioral metrics that define this segment
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {selectedSegmentData.criteria && selectedSegmentData.criteria.length > 0 ? (
                            <div className="space-y-3">
                              {/* Display actual criteria from database */}
                              {selectedSegmentData.criteria.map((criterion, index) => {
                                // Format the display value with unit
                                let displayValue = `${criterion.operator} ${criterion.value}`;
                                if (criterion.unit && criterion.unit !== '') {
                                  displayValue = `${criterion.operator} ${criterion.value} ${criterion.unit}`;
                                }
                                
                                // Special formatting for certain operators
                                if (criterion.operator === 'is' || criterion.operator === 'is not') {
                                  displayValue = `${criterion.operator} ${criterion.value}`;
                                } else if (!['>', '<', '≥', '≤', '='].includes(criterion.operator)) {
                                  // For text-based operators like frequency values
                                  displayValue = criterion.operator || criterion.value;
                                }
                                
                                return (
                                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {criterion.metric}
                                    </span>
                                    <Badge variant="outline" className="ml-2">
                                      {displayValue}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            /* Default criteria for segments without specific criteria */
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Engagement</h4>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Visits (30 days)</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'deal-hunters' ? '≥ 5' : 
                                       selectedSegment === 'research-enthusiasts' ? '≥ 8' :
                                       selectedSegment === 'premium-shoppers' ? '≥ 3' : '≥ 2'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Avg Session Duration</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'research-enthusiasts' ? '> 5 min' :
                                       selectedSegment === 'premium-shoppers' ? '> 3 min' : '> 2 min'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Pages per Session</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'research-enthusiasts' ? '≥ 10' :
                                       selectedSegment === 'deal-hunters' ? '≥ 6' : '≥ 4'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Behavior</h4>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Purchase Frequency</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'premium-shoppers' ? 'Weekly' :
                                       selectedSegment === 'quick-buyers' ? 'Bi-weekly' : 'Monthly'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Cart Abandonment</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'quick-buyers' ? '< 10%' :
                                       selectedSegment === 'premium-shoppers' ? '< 15%' : '< 30%'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Discount Usage</span>
                                    <Badge variant="outline">
                                      {selectedSegment === 'deal-hunters' ? 'Very High' :
                                       selectedSegment === 'premium-shoppers' ? 'Low' : 'Medium'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Additional Information */}
                          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              <Info className="w-3 h-3 inline mr-1" />
                              Segments are automatically updated based on user behavior. Users can belong to multiple segments with different confidence scores.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  </Tabs>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* AI Chat Dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/crew-agents/customer-segmentation-agent.jpg" 
                  alt="Customer Segmentation Agent"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <DialogTitle>Customer Segmentation Agent</DialogTitle>
                  <p className="text-sm text-gray-600">
                    {selectedSegmentData 
                      ? `Discussing insights for ${selectedSegmentData.name} segment`
                      : 'Customer segmentation analysis and recommendations'
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setChatOpen(false)}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          
          <div className="h-[60vh]">
            <StreamingChatWidget
              position="embedded"
              siteId={selectedSite?._id || selectedSite?.id || ''}
              sessionId={`segment-chat-${selectedSegment}-${Date.now()}`}
              visitorId={user?.id || 'admin-user'}
              pageContent={selectedSegmentData ? 
                `Customer Segment: ${selectedSegmentData.name}\n` +
                `Description: ${selectedSegmentData.description}\n` +
                `User Count: ${selectedSegmentData.userCount}\n` +
                `Percentage: ${selectedSegmentData.percentage.toFixed(1)}%\n` +
                `Characteristics: ${selectedSegmentData.characteristics.join(', ')}\n` +
                `Average Order Value: $${selectedSegmentData.avgOrderValue?.toFixed(2) || '125.50'}\n` +
                `Conversion Rate: ${selectedSegmentData.conversionRate?.toFixed(2) || '12.50'}%\n` +
                `Current Recommendation: ${SEGMENT_RECOMMENDATIONS[selectedSegment] || 'No specific recommendation available'}`
                : 'Customer segmentation analysis for your site'
              }
              pageUrl={window.location.href}
              welcomeMessage={selectedSegmentData ? 
                `Hi! I'm your Customer Segmentation Agent. I can see you're looking at the "${selectedSegmentData.name}" segment with ${selectedSegmentData.userCount} users (${selectedSegmentData.percentage.toFixed(1)}% of your audience). Let's discuss strategies to better engage this segment!` :
                "Hi! I'm your Customer Segmentation Agent. I can help you understand your customer segments, analyze behavior patterns, and develop targeted marketing strategies. What would you like to know?"
              }
              placeholder={`Ask about ${selectedSegmentData?.name || 'customer segments'}...`}
              useStreaming={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}